import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Upload, FileText, TrendingUp, AlertCircle, CheckCircle, Clock, Target, Plus, Library, Coins } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { JobLibraryService, SavedJobDescription } from "@/services/jobLibraryService";
import { JobScrapingService } from "@/services/jobScrapingService";
import { DocumentUpload } from "@/components/DocumentUpload";
import { ResumeLibraryService, SavedResume } from "@/services/resumeLibraryService";
import { ResumeMatchTestService } from "@/services/resumeMatchTestService";
import { ResumeService } from "@/services/resumeService";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCredits } from "@/hooks/useCredits";
import { UpgradePrompt } from "@/components/UpgradePrompt";

interface ResumeAnalysisProps {
  onBack: () => void;
}

interface AnalysisResults {
  analysis: string;
  success: boolean;
}

type ResumeInputMethod = 'upload' | 'text' | 'library';

const MATCH_SCORE_CREDIT_COST = 3;

export function ResumeAnalysis({ onBack }: ResumeAnalysisProps) {
  const [currentStep, setCurrentStep] = useState<'choose-job-source' | 'select-job' | 'upload-resume' | 'results'>('choose-job-source');
  const [selectedJob, setSelectedJob] = useState<SavedJobDescription | null>(null);
  const [savedJobs, setSavedJobs] = useState<SavedJobDescription[]>([]);
  const [savedResumes, setSavedResumes] = useState<SavedResume[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeText, setResumeText] = useState<string>('');
  const [selectedResume, setSelectedResume] = useState<SavedResume | null>(null);
  const [resumeInputMethod, setResumeInputMethod] = useState<ResumeInputMethod>('upload');
  const [newJobDescription, setNewJobDescription] = useState('');
  const [newJobTitle, setNewJobTitle] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [jobUrl, setJobUrl] = useState('');
  const [isScrapingUrl, setIsScrapingUrl] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { balance, deductCredits, isLoading: creditsLoading } = useCredits();

  useEffect(() => {
    if (user) {
      loadSavedJobs();
      loadSavedResumes();
    }
  }, [user]);

  const loadSavedJobs = async () => {
    setIsLoading(true);
    try {
      const jobs = await JobLibraryService.getSavedJobs();
      setSavedJobs(jobs);
    } catch (error) {
      console.error('Error loading saved jobs:', error);
      toast({
        title: "Error",
        description: "Failed to load saved jobs. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewJob = () => {
    if (!newJobTitle.trim() || !newCompanyName.trim() || !newJobDescription.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in job title, company name, and job description.",
        variant: "destructive",
      });
      return;
    }

    // Create a temporary job object without saving to database
    const tempJob: SavedJobDescription = {
      id: `temp-${Date.now()}`,
      jobTitle: newJobTitle,
      companyName: newCompanyName,
      companyMission: '',
      selectedResponsibilities: [],
      selectedRequirements: [],
      selectedSkills: [],
      allResponsibilities: [newJobDescription], // Use full description as single responsibility
      allRequirements: [],
      allSkills: [],
      matchScore: 0,
      dateCreated: new Date().toISOString()
    };

    setSelectedJob(tempJob);
    setCurrentStep('upload-resume');
    
    toast({
      title: "Job Ready for Analysis!",
      description: `${newJobTitle} at ${newCompanyName} is ready for resume analysis.`,
    });
  };

  const handleJobSelect = (job: SavedJobDescription) => {
    setSelectedJob(job);
    setCurrentStep('upload-resume');
  };

  const loadSavedResumes = async () => {
    setIsLoading(true);
    try {
      const resumes = await ResumeLibraryService.getSavedResumes();
      setSavedResumes(resumes);
    } catch (error) {
      console.error('Error loading saved resumes:', error);
      toast({
        title: "Error",
        description: "Failed to load saved resumes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentUpload = (text: string, filename?: string) => {
    setResumeText(text);
    if (filename) {
      // Create a mock file object for tracking
      const blob = new Blob([text], { type: 'text/plain' });
      const file = new File([blob], filename, { type: 'text/plain' });
      setResumeFile(file);
    }
  };

  const getResumeText = async (): Promise<string> => {
    switch (resumeInputMethod) {
      case 'upload':
        if (!resumeText.trim()) throw new Error('No resume content extracted');
        return resumeText.trim();
      
      case 'text':
        if (!resumeText.trim()) throw new Error('No resume text provided');
        return resumeText.trim();
      
      case 'library':
        if (!selectedResume) throw new Error('No resume selected from library');
        return selectedResume.content;
      
      default:
        throw new Error('Invalid resume input method');
    }
  };

  const analyzeResume = async () => {
    if (!selectedJob) return;

    // Check credits before analyzing
    if (user && balance < MATCH_SCORE_CREDIT_COST) {
      setShowUpgradePrompt(true);
      return;
    }

    setIsAnalyzing(true);
    try {
      // Credits will be deducted AFTER successful analysis and save

      // Get resume text based on input method
      const resumeTextContent = await getResumeText();
      
      // Prepare job description data using ALL fields for complete analysis
      const jobDescription = {
        company_name: selectedJob.companyName,
        job_title: selectedJob.jobTitle,
        company_mission: selectedJob.companyMission,
        responsibilities: selectedJob.allResponsibilities || [],
        requirements: selectedJob.allRequirements || [],
        skills: selectedJob.allSkills || []
      };

      // Call the analyze-resume-match edge function
      const { data, error } = await supabase.functions.invoke('analyze-resume-match', {
        body: {
          resumeText: resumeTextContent,
          jobDescription,
          clientName: user?.user_metadata?.first_name || 'Applicant'
        }
      });

      if (error) throw error;

      setAnalysisResults(data);
      setCurrentStep('results');
      
      // Save the analyzed resume to the library with analysis data
      try {
        // Extract person's name from resume content (only for text uploads)
        const extractPersonName = (text: string): string => {
          // Look for common name patterns at the beginning of the resume
          const lines = text.split('\n').slice(0, 10); // Check first 10 lines
          
          for (const line of lines) {
            const trimmed = line.trim();
            // Skip empty lines, emails, phones, addresses
            if (!trimmed || trimmed.includes('@') || trimmed.includes('Phone') || 
                trimmed.includes('Email') || trimmed.includes('Address') ||
                trimmed.length < 3 || trimmed.length > 50) continue;
            
            // Check if line looks like a name (2-4 words, each capitalized)
            const words = trimmed.split(/\s+/);
            if (words.length >= 2 && words.length <= 4) {
              const isName = words.every(word => 
                /^[A-Z][a-z]+$/.test(word) || /^[A-Z]\.?$/.test(word)
              );
              if (isName) {
                return words.slice(0, 2).join(' '); // Take first and last name
              }
            }
          }
          return 'Unknown Name';
        };

        // Generate resume title based on upload method
        const currentYear = new Date().getFullYear();
        let resumeTitle = '';
        
        if (resumeInputMethod === 'upload' && resumeFile) {
          // For file uploads, keep original filename
          const fileName = resumeFile.name.replace(/\.[^/.]+$/, "");
          resumeTitle = `${fileName} - ${selectedJob.jobTitle} Resume ${currentYear}`;
        } else if (resumeInputMethod === 'text') {
          // For text uploads, extract person name
          const personName = extractPersonName(resumeTextContent);
          resumeTitle = `${personName} - ${selectedJob.jobTitle} Resume ${currentYear}`;
        } else if (resumeInputMethod === 'library' && selectedResume) {
          resumeTitle = `${selectedResume.title} - ${selectedJob.jobTitle}`;
        }

        // Extract match score from analysis results - parse from text
        let matchScore = 0;
        if (data.analysis) {
          // Try to extract score from the analysis text
          const analysisText = typeof data.analysis === 'string' ? data.analysis : JSON.stringify(data.analysis);
          
          // Look for patterns like "Overall Resume Match Score: 15%" or "match score is 15%"
          const scoreMatch = analysisText.match(/(?:overall\s+resume\s+match\s+score|match\s+score(?:\s+is)?)\s*:?\s*(\d+)%/i);
          if (scoreMatch) {
            matchScore = parseInt(scoreMatch[1], 10);
          } else {
            // Fallback: look for any percentage in the first part of the analysis
            const percentMatch = analysisText.match(/(\d+)%/);
            if (percentMatch) {
              matchScore = parseInt(percentMatch[1], 10);
            }
          }
        }

        console.log('Extracted match score:', matchScore, 'from analysis:', data.analysis);

        // Save to resume_analyses table instead
        if (!user?.id) throw new Error('User ID not available');

        // Create a temporary file if we're using text input
        let fileToUpload: File | undefined = resumeFile;
        if (resumeInputMethod === 'text') {
          // Create a blob from text content
          const blob = new Blob([resumeTextContent], { type: 'text/plain' });
          fileToUpload = new File([blob], `${resumeTitle}.txt`, { type: 'text/plain' });
        } else if (resumeInputMethod === 'library' && selectedResume) {
          // Create a file from library content
          const blob = new Blob([resumeTextContent], { type: selectedResume.fileType || 'text/plain' });
          fileToUpload = new File([blob], selectedResume.fileName || `${resumeTitle}.txt`, { 
            type: selectedResume.fileType || 'text/plain' 
          });
        }

        if (fileToUpload) {
          // Use ResumeService to upload and create analysis record
          const { analysis, error: saveError } = await ResumeService.uploadResume(user.id, {
            resume: fileToUpload,
            jobDescription: JSON.stringify(jobDescription),
            jobTitle: selectedJob.jobTitle,
            industry: selectedJob.companyName,
            targetingMethod: 'description'
          });

          if (saveError || !analysis) {
            throw new Error('Failed to save resume analysis');
          }

          // Update the analysis with results and extracted text
          const analysisWithResults = {
            ...data,
            match_score: matchScore,
            jobRequirements: selectedJob.selectedRequirements || [],
            jobResponsibilities: selectedJob.selectedResponsibilities || [],
            jobSkills: selectedJob.selectedSkills || []
          };

          await ResumeService.updateAnalysisResults(analysis.id, analysisWithResults);
          
          // Also save the extracted text
          const { error: updateError } = await supabase
            .from('resume_analyses')
            .update({ extracted_text: resumeTextContent })
            .eq('id', analysis.id);
            
          if (updateError) {
            console.error('Failed to save extracted text:', updateError);
          }

          // Save to resume match tests library for easy access
          try {
            await ResumeMatchTestService.saveResumeMatchTest(
              resumeTitle,
              resumeTextContent,
              matchScore,
              selectedJob.jobTitle,
              selectedJob.companyName,
              analysisWithResults,
              fileToUpload?.name,
              fileToUpload?.type
            );
            
            // NOW deduct credits after successful analysis and save
            if (user) {
              const creditResult = await deductCredits('resume_match_analysis', { 
                jobTitle: selectedJob.jobTitle,
                companyName: selectedJob.companyName,
                matchScore
              });
              if (creditResult.success) {
                toast({
                  title: "Analysis Complete",
                  description: `Your resume analysis has been completed and saved! ${creditResult.credits_used || 1} credit used.`,
                });
              } else {
                toast({
                  title: "Analysis Complete",
                  description: "Your resume analysis has been completed and saved! Note: Credit deduction failed.",
                });
              }
            } else {
              toast({
                title: "Analysis Complete",
                description: "Your resume analysis has been completed and saved to both libraries!",
              });
            }
          } catch (matchTestError) {
            console.error('Failed to save to match tests library:', matchTestError);
            // Don't fail the whole operation if this fails
            toast({
              title: "Analysis Complete",
              description: "Your resume analysis has been completed and saved!",
            });
          }
        }
      } catch (saveError) {
        console.error('Error saving resume to library:', saveError);
        // Analysis was successful but save failed - don't charge credits
        toast({
          title: "Analysis Complete",
          description: "Your resume analysis is complete but couldn't be saved to the library.",
        });
      }

    } catch (error) {
      console.error('Error analyzing resume:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to analyze your resume. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const canProceedToAnalysis = (): boolean => {
    if (!selectedJob) return false;
    
    switch (resumeInputMethod) {
      case 'upload':
        return resumeText.trim().length > 0;
      case 'text':
        return resumeText.trim().length > 0;
      case 'library':
        return !!selectedResume;
      default:
        return false;
    }
  };

  const resetResumeInputs = () => {
    setResumeFile(null);
    setResumeText('');
    setSelectedResume(null);
  };

  const renderStepIndicator = () => {
    const steps = [
      { key: 'choose-job-source', label: 'Choose Job Source', icon: Library },
      { key: 'select-job', label: 'Select Job', icon: Target },
      { key: 'upload-resume', label: 'Upload Resume', icon: Upload },
      { key: 'results', label: 'View Results', icon: TrendingUp }
    ];

    return (
      <div className="flex items-center justify-center mb-8 space-x-4">
        {steps.map((step, index) => {
          const isActive = currentStep === step.key;
          const isCompleted = steps.findIndex(s => s.key === currentStep) > index;
          const Icon = step.icon;
          
          return (
            <div key={step.key} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                isActive ? 'border-primary bg-primary text-primary-foreground' :
                isCompleted ? 'border-primary bg-primary text-primary-foreground' :
                'border-muted-foreground text-muted-foreground'
              }`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className={`ml-2 text-sm font-medium ${
                isActive ? 'text-primary' : 'text-muted-foreground'
              }`}>
                {step.label}
              </span>
              {index < steps.length - 1 && (
                <div className={`ml-4 w-8 h-0.5 ${
                  isCompleted ? 'bg-primary' : 'bg-muted'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please log in to access resume analysis.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Clean Header with TailorWiz Branding */}
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onBack}
                  className="h-9 w-9 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold text-foreground">Resume Analysis</h1>
                  <p className="text-sm text-muted-foreground">Get detailed insights on your resume match</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
                <span className="text-xs font-medium text-muted-foreground">powered by</span>
                <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto py-8 px-6 max-w-5xl">
          <div className="w-full">
            {/* Main Content Area */}
            <div className="flex-1 max-w-full">
              {/* Header Section */}
              <div className="mb-12 text-center">
                <div className="space-y-2 mb-8">
                  <h1 className="text-6xl font-extrabold leading-tight tracking-tight">
                    <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white dark:via-slate-100 dark:to-white bg-clip-text text-transparent">
                      Resume{" "}
                    </span>
                    <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
                      Analysis
                    </span>
                  </h1>
                  
                  <div className="max-w-4xl mx-auto px-4 mt-4">
                    <p className="text-lg font-semibold bg-gradient-to-r from-primary/80 to-accent/80 bg-clip-text text-transparent leading-relaxed drop-shadow-lg">
                      Get detailed insights on how well your resume matches job requirements
                    </p>
                  </div>
                </div>
              </div>

              {renderStepIndicator()}

          {/* Step 0: Choose Job Source */}
          {currentStep === 'choose-job-source' && (
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Library className="w-5 h-5 mr-2" />
                    Choose Job Source
                  </CardTitle>
                  <CardDescription>
                    Select how you'd like to provide the job information for analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="new-job" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="new-job" className="flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Paste Job Description
                      </TabsTrigger>
                      <TabsTrigger value="job-url" className="flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Enter Job URL
                      </TabsTrigger>
                      <TabsTrigger value="library" className="flex items-center gap-2">
                        <Library className="w-4 h-4" />
                        From Library ({savedJobs.length})
                      </TabsTrigger>
                    </TabsList>

                    {/* Paste New Job Description Tab */}
                    <TabsContent value="new-job" className="mt-6">
                      <Card className="border-primary/20">
                        <CardHeader>
                          <CardTitle className="text-lg">Enter Job Details Manually</CardTitle>
                          <CardDescription>
                            Paste or type the job information directly for instant analysis
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="jobTitle">Job Title *</Label>
                              <Input
                                id="jobTitle"
                                placeholder="e.g. Software Engineer"
                                value={newJobTitle}
                                onChange={(e) => setNewJobTitle(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="companyName">Company Name *</Label>
                              <Input
                                id="companyName"
                                placeholder="e.g. Google"
                                value={newCompanyName}
                                onChange={(e) => setNewCompanyName(e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="jobDescription">Job Description *</Label>
                            <Textarea
                              id="jobDescription"
                              placeholder="Paste the complete job description here..."
                              className="min-h-[150px]"
                              value={newJobDescription}
                              onChange={(e) => setNewJobDescription(e.target.value)}
                            />
                          </div>
                          <Button 
                            onClick={handleCreateNewJob}
                            className="w-full"
                            disabled={!newJobTitle.trim() || !newCompanyName.trim() || !newJobDescription.trim()}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Continue with This Job
                          </Button>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Job URL Tab */}
                    <TabsContent value="job-url" className="mt-6">
                      <Card className="border-blue-200">
                        <CardHeader>
                          <CardTitle className="text-lg">Extract from Job URL</CardTitle>
                          <CardDescription>
                            Paste a job posting URL and we'll automatically extract the job details
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="jobUrl">Job Posting URL *</Label>
                            <Input
                              id="jobUrl"
                              placeholder="https://company.com/careers/job-posting"
                              type="url"
                              value={jobUrl}
                              onChange={(e) => setJobUrl(e.target.value)}
                            />
                          </div>
                          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                              <div className="text-sm">
                                <p className="font-medium text-blue-900 mb-1">Supported Sites:</p>
                                <p className="text-blue-700">LinkedIn, Indeed, Glassdoor, Monster, ZipRecruiter, and most company career pages</p>
                              </div>
                            </div>
                          </div>
                          <Button 
                            className="w-full"
                            disabled={!jobUrl.trim() || isScrapingUrl}
                            onClick={async () => {
                              if (!jobUrl.trim()) return;
                              setIsScrapingUrl(true);
                              try {
                                const result = await JobScrapingService.scrapeJobUrl(jobUrl);
                                if (result.success && result.content) {
                                  // Try to extract title and company from content
                                  const lines = result.content.split('\n').filter(l => l.trim());
                                  const extractedTitle = lines[0]?.substring(0, 100) || 'Job Position';
                                  const extractedCompany = lines.find(l => l.toLowerCase().includes('company') || l.toLowerCase().includes('about'))?.substring(0, 50) || 'Company';
                                  
                                  const tempJob: SavedJobDescription = {
                                    id: `url-${Date.now()}`,
                                    jobTitle: extractedTitle,
                                    companyName: extractedCompany,
                                    companyMission: '',
                                    selectedResponsibilities: [],
                                    selectedRequirements: [],
                                    selectedSkills: [],
                                    allResponsibilities: [result.content],
                                    allRequirements: [],
                                    allSkills: [],
                                    matchScore: 0,
                                    dateCreated: new Date().toISOString()
                                  };
                                  setSelectedJob(tempJob);
                                  setCurrentStep('upload-resume');
                                  toast({
                                    title: "Job Extracted!",
                                    description: "Job details have been extracted. Now upload your resume for analysis.",
                                  });
                                } else {
                                  throw new Error(result.error || 'Failed to extract job details');
                                }
                              } catch (error: any) {
                                toast({
                                  title: "Extraction Failed",
                                  description: error.message || "Could not extract job details. Try pasting the job description manually.",
                                  variant: "destructive",
                                });
                              } finally {
                                setIsScrapingUrl(false);
                              }
                            }}
                          >
                            {isScrapingUrl ? (
                              <>
                                <Clock className="w-4 h-4 mr-2 animate-spin" />
                                Extracting...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Extract Job Details
                              </>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Library Selection Tab */}
                    <TabsContent value="library" className="mt-6">
                      <Card className="border-green-200">
                        <CardHeader>
                          <CardTitle className="text-lg">Choose from Saved Jobs</CardTitle>
                          <CardDescription>
                            Select from your previously saved job descriptions ({savedJobs.length} available)
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {savedJobs.length === 0 ? (
                            <div className="text-center py-8">
                              <Library className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                              <h3 className="text-lg font-medium mb-2">No saved jobs found</h3>
                              <p className="text-muted-foreground mb-4">
                                You need to save some job descriptions first. Use the "Paste Job Description" tab above to get started.
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <div className="grid gap-3 max-h-60 overflow-y-auto">
                                {savedJobs.slice(0, 5).map((job) => (
                                  <Card 
                                    key={job.id} 
                                    className="cursor-pointer hover:bg-muted/50 transition-colors border-l-4 border-l-green-500"
                                    onClick={() => handleJobSelect(job)}
                                  >
                                    <CardContent className="p-4">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <h3 className="font-semibold">{job.jobTitle}</h3>
                                          <p className="text-sm text-muted-foreground">{job.companyName}</p>
                                          <p className="text-xs text-muted-foreground">
                                            Saved {new Date(job.dateCreated).toLocaleDateString()}
                                          </p>
                                        </div>
                                        <ArrowLeft className="w-4 h-4 rotate-180 text-muted-foreground" />
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                              {savedJobs.length > 5 && (
                                <Button 
                                  variant="outline" 
                                  className="w-full"
                                  onClick={() => setCurrentStep('select-job')}
                                >
                                  <Library className="w-4 h-4 mr-2" />
                                  View All {savedJobs.length} Saved Jobs
                                </Button>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          )}

        {/* Step 1: Select Job */}
        {currentStep === 'select-job' && (
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Target className="w-5 h-5 mr-2" />
                  Select a Job from Your Library
                </CardTitle>
                <CardDescription>
                  Choose a saved job description to analyze your resume against
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : savedJobs.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No saved jobs found</h3>
                    <p className="text-muted-foreground mb-4">
                      You need to save some job descriptions first before analyzing resumes.
                    </p>
                    <Button onClick={onBack}>
                      Add Jobs to Library
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {savedJobs.map((job) => (
                      <Card 
                        key={job.id} 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleJobSelect(job)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-6">
                            {/* Job Info */}
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg">{job.jobTitle}</h3>
                              <p className="text-muted-foreground">{job.companyName}</p>
                              <div className="flex items-center mt-2 space-x-4 text-sm text-muted-foreground">
                                <span>{job.allResponsibilities?.length || 0} responsibilities</span>
                                <span>{job.allRequirements?.length || 0} requirements</span>
                                <span>{job.allSkills?.length || 0} skills</span>
                              </div>
                            </div>

                            {/* Match Score Circle */}
                            <div className={`w-20 h-20 rounded-full border-2 flex flex-col items-center justify-center flex-shrink-0 shadow-sm ${
                              job.matchScore >= 80 
                                ? "text-green-600 border-green-700 bg-green-50" 
                                : job.matchScore >= 56 
                                ? "text-yellow-600 border-yellow-700 bg-yellow-50" 
                                : job.matchScore >= 30 
                                ? "text-orange-600 border-orange-700 bg-orange-50" 
                                : "text-red-600 border-red-700 bg-red-50"
                            }`}>
                              <div className="text-sm font-bold">{job.matchScore}%</div>
                              <div className="text-xs font-medium text-center leading-tight">
                                <div>{job.matchScore >= 80 ? "Excellent" : job.matchScore >= 56 ? "Good" : job.matchScore >= 30 ? "Fair" : "Poor"}</div>
                                <div>Match</div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Upload Resume */}
        {currentStep === 'upload-resume' && selectedJob && (
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Upload className="w-5 h-5 mr-2" />
                  Select Your Resume
                </CardTitle>
                <CardDescription>
                  Analyzing against: <strong>{selectedJob.jobTitle}</strong> at <strong>{selectedJob.companyName}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={resumeInputMethod} onValueChange={(value) => {
                  setResumeInputMethod(value as ResumeInputMethod);
                  resetResumeInputs();
                }}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="upload">Upload File</TabsTrigger>
                    <TabsTrigger value="text">Paste Text</TabsTrigger>
                    <TabsTrigger value="library">From Library</TabsTrigger>
                  </TabsList>
                  
                  {/* Upload File Tab */}
                  <TabsContent value="upload">
                    <DocumentUpload
                      onUploadComplete={handleDocumentUpload}
                      title="Upload Resume"
                      description="Upload a PDF, Word document, or text file"
                      acceptedTypes={['.pdf', '.doc', '.docx', '.txt']}
                    />
                  </TabsContent>
                  
                  {/* Paste Text Tab */}
                  <TabsContent value="text">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold mb-2">Paste Your Resume Text</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Copy and paste the text content of your resume below
                        </p>
                        <textarea
                          value={resumeText}
                          onChange={(e) => setResumeText(e.target.value)}
                          placeholder="Paste your resume text here..."
                          className="w-full h-64 p-4 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                          disabled={isAnalyzing}
                        />
                      </div>
                      {resumeText.trim().length > 0 && (
                        <div className="text-sm text-muted-foreground">
                          Character count: {resumeText.length} | Word count: ~{resumeText.trim().split(/\s+/).length}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  
                  {/* From Library Tab */}
                  <TabsContent value="library">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold mb-2">Select from Saved Resumes</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Choose a resume from your library
                        </p>
                      </div>
                      
                      {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      ) : savedResumes.length === 0 ? (
                        <div className="text-center py-8">
                          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                          <h4 className="text-lg font-medium mb-2">No saved resumes found</h4>
                          <p className="text-muted-foreground">
                            Upload a resume file or paste text to save it to your library.
                          </p>
                        </div>
                      ) : (
                        <div className="grid gap-3 max-h-64 overflow-y-auto">
                          {savedResumes.map((resume) => (
                            <Card 
                              key={resume.id} 
                              className={`cursor-pointer transition-colors p-3 ${
                                selectedResume?.id === resume.id 
                                  ? 'bg-primary/10 border-primary' 
                                  : 'hover:bg-muted/50'
                              }`}
                              onClick={() => setSelectedResume(resume)}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h4 className="font-medium">{resume.title}</h4>
                                  <p className="text-sm text-muted-foreground">
                                    {resume.fileName || 'Text resume'} • 
                                    Created {new Date(resume.dateCreated).toLocaleDateString()}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    ~{resume.content.trim().split(/\s+/).length} words
                                  </p>
                                </div>
                                {selectedResume?.id === resume.id && (
                                  <CheckCircle className="w-5 h-5 text-primary" />
                                )}
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                <Separator className="my-6" />

                {/* Credit Cost Display */}
                {user && (
                  <Card className="border-primary/20 bg-primary/5 mb-4">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Coins className="w-5 h-5 text-primary" />
                          <span className="font-medium">Credit Cost:</span>
                          <span className="text-lg font-bold text-primary">{MATCH_SCORE_CREDIT_COST} credits</span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Your balance: <span className="font-semibold">{balance} credits</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                <div className="flex justify-between items-center">
                  <Button 
                    variant="outline" 
                    onClick={() => setCurrentStep('select-job')}
                    disabled={isAnalyzing}
                  >
                    Back to Job Selection
                  </Button>
                  
                  <Button 
                    onClick={analyzeResume}
                    disabled={!canProceedToAnalysis() || isAnalyzing || creditsLoading || (user && balance < MATCH_SCORE_CREDIT_COST)}
                    className="min-w-32"
                  >
                    {isAnalyzing ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : user && balance < MATCH_SCORE_CREDIT_COST ? (
                      <>
                        <Coins className="w-4 h-4 mr-2" />
                        Insufficient Credits
                      </>
                    ) : (
                      <>
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Analyze Resume ({MATCH_SCORE_CREDIT_COST} credits)
                      </>
                    )}
                  </Button>
                </div>

                {/* Upgrade Prompt Modal */}
                {showUpgradePrompt && (
                  <UpgradePrompt
                    feature="Resume Match Analysis"
                    creditsNeeded={MATCH_SCORE_CREDIT_COST}
                    onDismiss={() => setShowUpgradePrompt(false)}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Results */}
        {currentStep === 'results' && analysisResults && (
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Resume Analysis Results
                </CardTitle>
                <CardDescription>
                  Complete analysis for {selectedJob?.jobTitle} at {selectedJob?.companyName}
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Match Score Card */}
            <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
              <CardContent className="p-6">
                <div className="text-center">
                  <h2 className="text-2xl font-bold mb-4 flex items-center justify-center gap-2">
                    <Target className="w-6 h-6 text-primary" />
                    Resume Match Score
                  </h2>
                  {(() => {
                    // Extract match score from analysis results
                    let matchScore = 0;
                    if (analysisResults.analysis) {
                      const analysisText = typeof analysisResults.analysis === 'string' ? analysisResults.analysis : JSON.stringify(analysisResults.analysis);
                      const scoreMatch = analysisText.match(/(?:overall\s+resume\s+match\s+score|match\s+score(?:\s+is)?)\s*:?\s*(\d+)%/i);
                      if (scoreMatch) {
                        matchScore = parseInt(scoreMatch[1], 10);
                      } else {
                        const percentMatch = analysisText.match(/(\d+)%/);
                        if (percentMatch) {
                          matchScore = parseInt(percentMatch[1], 10);
                        }
                      }
                    }

                    const getScoreColor = (score: number) => {
                      if (score >= 80) return "text-green-600";
                      if (score >= 56) return "text-yellow-600"; 
                      if (score >= 30) return "text-orange-600";
                      return "text-red-600";
                    };

                    const getScoreLabel = (score: number) => {
                      if (score >= 80) return "Excellent Match";
                      if (score >= 56) return "Good Match";
                      if (score >= 30) return "Fair Match";  
                      return "Poor Match";
                    };

                    const getQualificationStatus = (score: number) => {
                      return score >= 70 ? "Congrats! Your Resume is Ready to Apply for This Job!" : "Your Resume is NOT Ready for You to Apply for This Job.";
                    };

                    return (
                      <>
                        <div className="flex items-center justify-center gap-6 mb-6">
                          <div className={`text-5xl font-bold ${getScoreColor(matchScore)}`}>
                            {matchScore}%
                          </div>
                          <div className="text-left">
                            <p className={`text-xl font-semibold ${getScoreColor(matchScore)}`}>
                              {getScoreLabel(matchScore)}
                            </p>
                            <p className="text-muted-foreground">
                              Resume compatibility analysis
                            </p>
                          </div>
                        </div>

                        {/* Qualification Status */}
                        <div className={`rounded-lg border p-4 ${matchScore >= 70 ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                          <h3 className={`text-lg font-bold ${matchScore >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                            {getQualificationStatus(matchScore)}
                          </h3>
                          {matchScore < 70 && (
                            <div className="mt-3">
                              <Button 
                                onClick={() => navigate('/resume-tailoring-fixed-duplicate')}
                                className="bg-primary hover:bg-primary/90"
                              >
                                Use TailorWiz to Tailor it Now
                              </Button>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>

            {/* Detailed Analysis Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  📋 Detailed Analysis Report
                </CardTitle>
                <CardDescription>
                  Comprehensive breakdown of your resume match
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <div className="bg-muted/50 rounded-lg p-6">
                    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
                      {analysisResults.analysis}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card>
              <CardContent className="p-6">
                <div className="flex gap-4 justify-center">
                  <Button onClick={() => {
                    setCurrentStep('choose-job-source');
                    setSelectedJob(null);
                    resetResumeInputs();
                    setAnalysisResults(null);
                  }}>
                    Analyze Another Resume
                  </Button>
                  <Button variant="outline" onClick={onBack}>
                    Back to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}